import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import WeAllTeamService from "@/api/weAllTeamService";

const defaultError = (err) => {
  const message =
    err?.response?.data?.message ||
    err?.message ||
    "An error occurred. Please try again.";
  toast.error(message);
};

export const INBOX_KEY = ["weall-team-inbox"];
export const CONVERSATION_KEY = ["weall-team-conversation"];
export const UNREAD_KEY = ["weall-team-unread"];

/** Users to pick from when starting a conversation. */
const useWeAllTeamUsers = (search) =>
  useQuery({
    queryKey: ["weall-team-users", search],
    queryFn: () => WeAllTeamService.searchUsers(search),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

/** Inbox page. The socket invalidates it, so it stays current without polling. */
const useWeAllTeamInbox = (params = {}) =>
  useQuery({
    queryKey: [...INBOX_KEY, params],
    queryFn: () => WeAllTeamService.getInbox(params),
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    placeholderData: (previous) => previous,
  });

/** One page of a conversation's history (page 1 = newest, and marks the conversation read). */
const useWeAllTeamConversation = (conversationId, page = 1) =>
  useQuery({
    queryKey: [...CONVERSATION_KEY, conversationId, page],
    queryFn: () =>
      WeAllTeamService.getConversation({
        conversation_id: conversationId,
        page,
      }),
    enabled: Boolean(conversationId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

const useWeAllTeamUnread = (enabled = true) =>
  useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => WeAllTeamService.getUnreadCount(),
    enabled,
    retry: 1,
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

const useSendWeAllTeamMessage = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => WeAllTeamService.send(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

const useReplyWeAllTeam = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => WeAllTeamService.reply(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useWeAllTeamUsers,
  useWeAllTeamInbox,
  useWeAllTeamConversation,
  useWeAllTeamUnread,
  useSendWeAllTeamMessage,
  useReplyWeAllTeam,
};
